import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindProductTypeValuesQueryResponse,
  FindProductTypeValuesQueryParams,
} from "../types/FindProductTypeValues"

type FindProductTypeValuesClient = typeof client<
  FindProductTypeValuesQueryResponse,
  Error,
  never
>
type FindProductTypeValues = {
  data: FindProductTypeValuesQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindProductTypeValuesQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindProductTypeValuesClient>>
  client: {
    parameters: Partial<Parameters<FindProductTypeValuesClient>[0]>
    return: Awaited<ReturnType<FindProductTypeValuesClient>>
  }
}
export const findProductTypeValuesQueryKey = (
  params: FindProductTypeValues["queryParams"]
) =>
  [
    { url: "/v1/product/find-product-type-values" },
    ...(params ? [params] : []),
  ] as const
export type FindProductTypeValuesQueryKey = ReturnType<
  typeof findProductTypeValuesQueryKey
>
export function findProductTypeValuesQueryOptions<
  TData = FindProductTypeValues["response"],
  TQueryData = FindProductTypeValues["response"],
>(
  params: FindProductTypeValues["queryParams"],
  options: FindProductTypeValues["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindProductTypeValues["response"],
    FindProductTypeValues["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findProductTypeValuesQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindProductTypeValues["data"],
        FindProductTypeValues["error"]
      >({
        method: "get",
        url: `/v1/product/find-product-type-values`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find product type values
 * @link /v1/product/find-product-type-values
 */
export function useFindProductTypeValues<
  TData = FindProductTypeValues["response"],
  TQueryData = FindProductTypeValues["response"],
  TQueryKey extends QueryKey = FindProductTypeValuesQueryKey,
>(
  params: FindProductTypeValues["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindProductTypeValues["response"],
        FindProductTypeValues["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindProductTypeValues["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindProductTypeValues["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findProductTypeValuesQueryKey(params)
  const query = useQuery<
    FindProductTypeValues["data"],
    FindProductTypeValues["error"],
    TData,
    any
  >({
    ...findProductTypeValuesQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindProductTypeValues["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
